// ghostLadder.ts — GHOST DUELS (Slice C) daily ghost-ladder state + rung selection. Two concerns, both
// pure/DOM-light so they smoke under plain `tsx` (the only window access is a lazy, try/caught localStorage
// read/write, exactly like lastGhost.ts / stats.ts):
//
//   1. PERSISTED PROGRESS (`trivia.ghostLadder.v1` {day, gateBeaten, rungsBeaten[]}) — the BYT3 gate + which
//      rungs the player has out-run TODAY. Auto-resets when the UTC day changes (a stale day → a fresh state
//      on read; the store is only rewritten on the next mutation), so the ladder is a per-day ritual.
//   2. RUNG SELECTION (`pickRungs`) — pure ranking math over the day's fetched ghosts (p25 → median → #1),
//      de-duplicated so a thin day (1–2 challengers) still yields distinct rungs.
//
// FAIL-CLOSED by construction: a blocked/absent/corrupt store degrades to a fresh (locked) state and every
// write is best-effort — the daily itself never depends on this module, so it can only ADD a section, never
// break the run. Presentation/meta only: never touches Core, the Sim, or determinism.

import type { GhostRecord } from '../net/types';

const KEY = 'trivia.ghostLadder.v1';

/** Persisted per-day ladder progress. `rungsBeaten` holds rung NUMBERS (1..3), not ghost indices. */
export interface GhostLadderState {
  /** UTC day key (YYYY-MM-DD) this progress belongs to; a mismatch on read → the state is reset. */
  day: string;
  /** true once today's daily was cleared with the rival engaged (the ladder unlock). */
  gateBeaten: boolean;
  /** which rungs (1..3) the player has out-run today (sorted, de-duplicated). */
  rungsBeaten: number[];
}

/**
 * A ladder challenger the player elected to race: the pin-verified ghost + the rung it occupies. Threaded
 * panel → MenuScene → GameScene (builds the racer) → ResultScene (marks the rung beaten + celebrates).
 */
export interface LadderRace {
  /** the challenger ghost to race (already bv/seed/mutator/topic-pinned to today's daily by the panel). */
  record: GhostRecord;
  /** which ladder rung (1..3) this ghost occupies — the key `markRungBeaten` persists. */
  rung: number;
}

/** UTC day key, kept local (mirrors stats.utcDayKey) so this module needn't import the stats surface. */
function todayKey(): string {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fresh(day: string): GhostLadderState {
  return { day, gateBeaten: false, rungsBeaten: [] };
}

/**
 * Read today's ladder progress, or a FRESH (locked) state on rollover / absence / corruption / a blocked
 * store. Read-only: a rollover does not rewrite the store (the next mutation does), so opening the panel on
 * a new day is a pure reset with no side effect. `day` is injectable for the smoke; production omits it.
 */
export function readLadder(day: string = todayKey()): GhostLadderState {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return fresh(day);
    const o = JSON.parse(raw) as Partial<GhostLadderState> | null;
    if (!o || typeof o !== 'object' || o.day !== day) return fresh(day); // rollover / corrupt → locked
    const rungs = Array.isArray(o.rungsBeaten)
      ? o.rungsBeaten.filter((n): n is number => typeof n === 'number' && Number.isFinite(n)).map((n) => Math.floor(n))
      : [];
    return { day, gateBeaten: o.gateBeaten === true, rungsBeaten: Array.from(new Set(rungs)).sort((a, b) => a - b) };
  } catch {
    return fresh(day);
  }
}

/** Best-effort persist (a blocked / full store is silently ignored — the ladder just won't remember). */
function write(s: GhostLadderState): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode / blocked / quota — progress is lost next session, never an error surface. */
  }
}

/** Unlock the ladder (idempotent). Returns the resulting state. */
export function markGateBeaten(day: string = todayKey()): GhostLadderState {
  const s = readLadder(day);
  if (s.gateBeaten) return s;
  s.gateBeaten = true;
  write(s);
  return s;
}

/** Mark a rung (1..3) out-run today (idempotent, de-duplicated, sorted). Returns the resulting state. */
export function markRungBeaten(rung: number, day: string = todayKey()): GhostLadderState {
  const r = Math.floor(rung);
  const s = readLadder(day);
  if (!s.rungsBeaten.includes(r)) {
    s.rungsBeaten.push(r);
    s.rungsBeaten.sort((a, b) => a - b);
    write(s);
  }
  return s;
}

/**
 * Pick the ladder rungs from the day's ghosts, ranked BEST-FIRST (caller sorts by score desc). The rung
 * numbers climb in difficulty: rung 1 = ~p25 (a beatable warm-up challenger), rung 2 = the median, rung 3 =
 * the day's #1. De-duplicated by source index so a thin day still yields DISTINCT challengers — n=1 → one
 * rung, n=2 → two, n≥3 → three — never the same ghost twice, and always ascending rung order.
 */
export function pickRungs(sortedDesc: readonly GhostRecord[]): LadderRace[] {
  const n = sortedDesc.length;
  if (n === 0) return [];
  // rung → source index (best-first): p25 is weakest (highest index), #1 is index 0.
  const candidates: Array<{ rung: number; idx: number }> = [
    { rung: 1, idx: Math.ceil(n * 0.75) - 1 }, // ~p25 (a weaker challenger)
    { rung: 2, idx: Math.floor((n - 1) / 2) }, // median
    { rung: 3, idx: 0 }, // #1
  ];
  const seen = new Set<number>();
  const out: LadderRace[] = [];
  for (const c of candidates) {
    if (c.idx < 0 || c.idx >= n) continue;
    if (seen.has(c.idx)) continue; // a collision on a thin day → drop the duplicate, keep the weaker rung
    seen.add(c.idx);
    out.push({ rung: c.rung, record: sortedDesc[c.idx] });
  }
  return out;
}
